import type {
  ProfileModuleDto,
  ProfileModuleType,
} from '@up2stay/up2promo-api-client';
import type { ProfileTemplateFieldMode } from '@up2stay/up2promo-api-client';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Settings } from 'lucide-react';
import { useState } from 'react';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import { getModuleId } from '@/core/lib/profile-module-entry-policies';
import type {
  BiographyModuleConfig,
  ContactInfoModuleConfig,
  FilesModuleConfig,
  GoogleMapsModuleConfig,
  ImagesModuleConfig,
  LinksModuleConfig,
  ModuleConfig,
  SocialsModuleConfig,
  TeamMembersModuleConfig,
  VideoModuleConfig,
} from '@/core/types/profile-modules';

import type { ModuleEntryGovernanceProps } from './module-entry-governance';
import { BiographyModuleConfigComponent } from './modules/biography-module-config';
import { ContactInfoModuleConfigComponent } from './modules/contact-info-module-config';
import { FilesModuleConfigComponent } from './modules/files-module-config';
import { GoogleMapsModuleConfigComponent } from './modules/google-maps-module-config';
import { ImagesModuleConfigComponent } from './modules/images-module-config';
import { LinksModuleConfigComponent } from './modules/links-module-config';
import { SocialsModuleConfigComponent } from './modules/socials-module-config';
import { TeamMembersModuleConfigComponent } from './modules/team-members-module-config';
import { VideoModuleConfigComponent } from './modules/video-module-config';

interface ModuleConfigWrapperProps extends ModuleEntryGovernanceProps {
  module: ProfileModuleDto;
  onConfigChange: (config: Record<string, unknown>) => void;
  /** Full content lock (e.g. template sync pending). */
  readOnly?: boolean;
  /** Template modules from assigned template (profile edit). */
  templateModules?: ProfileModuleDto[];
  /** 'dialog' = open config in a modal (default). 'inline' = render config form only, no button. */
  variant?: 'dialog' | 'inline';
  fieldLockedHintMessage?: string;
  /** Assigned teams on a profile template (team_members preview). */
  templateTeamIds?: string[];
  showManageTeamLink?: boolean;
}

function resolveTemplateModuleForGovernance(
  module: ProfileModuleDto,
  templateModules: ProfileModuleDto[] | undefined,
  showEntryPolicies: boolean,
  explicitTemplateModule?: ProfileModuleDto,
): ProfileModuleDto | undefined {
  if (explicitTemplateModule) return explicitTemplateModule;
  if (showEntryPolicies) return module;
  const moduleId = getModuleId(module);
  return templateModules?.find((m) => getModuleId(m) === moduleId);
}

function wholeModulePolicyFields(rawConfig: Record<string, unknown>): {
  policy?: ProfileTemplateFieldMode;
} {
  const policy = rawConfig.policy;
  if (typeof policy === 'string') {
    return { policy: policy as ProfileTemplateFieldMode };
  }
  return {};
}

/**
 * Helper function to convert raw config to typed config based on module type
 */
function getTypedConfig(
  type: ProfileModuleType,
  rawConfig: Record<string, unknown>,
): ModuleConfig | null {
  switch (type) {
    case 'biography':
      return {
        type: 'biography',
        content: (rawConfig.content as string) || '',
        format: (rawConfig.format as 'plain' | 'markdown' | 'html') || 'plain',
        showReadMore: rawConfig.showReadMore as boolean | undefined,
        maxLength: rawConfig.maxLength as number | undefined,
        ...wholeModulePolicyFields(rawConfig),
      } as BiographyModuleConfig;
    case 'contact_info': {
      return {
        type: 'contact_info',
        items: (rawConfig.items as ContactInfoModuleConfig['items']) ?? [],
        address: rawConfig.address as string | undefined,
        website: rawConfig.website as string | undefined,
        showPrivateData: rawConfig.showPrivateData as boolean | undefined,
        showBusinessData: rawConfig.showBusinessData as boolean | undefined,
      } as ContactInfoModuleConfig;
    }
    case 'socials': {
      const rawLayout = rawConfig.layout as string | undefined;
      const layout =
        rawLayout === 'list'
          ? 'list'
          : rawLayout === 'minimal'
            ? 'minimal'
            : 'grid';
      return {
        type: 'socials',
        links: (rawConfig.links as SocialsModuleConfig['links']) || [],
        layout,
      } as SocialsModuleConfig;
    }
    case 'links': {
      const rawLayout = rawConfig.layout as string | undefined;
      const layout = rawLayout === 'grid' ? 'grid' : 'list';
      return {
        type: 'links',
        items: (rawConfig.items as LinksModuleConfig['items']) || [],
        layout,
      } as LinksModuleConfig;
    }
    case 'video':
      return {
        type: 'video',
        url: (rawConfig.url as string) || '',
        title: rawConfig.title as string | undefined,
        description: rawConfig.description as string | undefined,
        ...wholeModulePolicyFields(rawConfig),
      } as VideoModuleConfig;
    case 'images':
      return {
        type: 'images',
        images: (rawConfig.images as ImagesModuleConfig['images']) || [],
        aspectRatio:
          (rawConfig.aspectRatio as ImagesModuleConfig['aspectRatio']) ?? '1:1',
      } as ImagesModuleConfig;
    case 'files':
      return {
        type: 'files',
        files: (rawConfig.files as FilesModuleConfig['files']) || [],
        layout: 'list',
        showDescriptions: rawConfig.showDescriptions as boolean | undefined,
        showFileSize: rawConfig.showFileSize as boolean | undefined,
      } as FilesModuleConfig;
    case 'google_maps':
      return {
        type: 'google_maps',
        locationId: rawConfig.locationId as string | undefined,
        address: (rawConfig.address as string) || '',
        latitude: rawConfig.latitude as number | undefined,
        longitude: rawConfig.longitude as number | undefined,
        zoom: (rawConfig.zoom as number) || 15,
        mapType:
          (rawConfig.mapType as GoogleMapsModuleConfig['mapType']) || 'roadmap',
        showMarker: rawConfig.showMarker as boolean | undefined,
        markerTitle: rawConfig.markerTitle as string | undefined,
        markerDescription: rawConfig.markerDescription as string | undefined,
        height: (rawConfig.height as number) || 400,
        allowFullscreen: rawConfig.allowFullscreen as boolean | undefined,
        ...wholeModulePolicyFields(rawConfig),
      } as GoogleMapsModuleConfig;
    case 'team_members':
      return {
        type: 'team_members',
        members:
          (rawConfig.members as TeamMembersModuleConfig['members']) || [],
        layout:
          (rawConfig.layout as TeamMembersModuleConfig['layout']) || 'grid',
        columns: (rawConfig.columns as number) || 3,
        showRoles: rawConfig.showRoles as boolean | undefined,
        showBios: rawConfig.showBios as boolean | undefined,
        showContactInfo: rawConfig.showContactInfo as boolean | undefined,
        showProfileLinks: rawConfig.showProfileLinks as boolean | undefined,
      } as TeamMembersModuleConfig;
    default:
      return null;
  }
}

export function ModuleConfigWrapper({
  module,
  onConfigChange,
  variant = 'dialog',
  structureReadOnly = false,
  templateModule: explicitTemplateModule,
  templateModules,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  templateOverrideLabels,
  overrideHintMessage,
  readOnly = false,
  templateTeamIds,
  showManageTeamLink = false,
  workspaceId,
  templateId,
  publishStatus,
  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: ModuleConfigWrapperProps) {
  const templateModule = resolveTemplateModuleForGovernance(
    module,
    templateModules,
    showEntryPolicies,
    explicitTemplateModule,
  );
  const governanceProps: ModuleEntryGovernanceProps = {
    structureReadOnly: structureReadOnly || readOnly,
    contentReadOnly: readOnly,
    templateModule,
    showEntryPolicies,
    onEntryPolicyChange,
    policyLabels,
    showEntryOverrideHints,
    templateOverrideLabels,
    overrideHintMessage,
    workspaceId,
    templateId,
    publishStatus,
    memberRoster,
    memberRosterLoading,
    hasAssignedTeams,
    memberValuesDict,
  };
  const [isOpen, setIsOpen] = useState(false);
  const typedConfig = getTypedConfig(module.type, module.config || {});

  if (!typedConfig) {
    return null;
  }

  const handleConfigChange = (newConfig: ModuleConfig) => {
    if (readOnly) return;
    const { type: _type, ...rawConfig } = newConfig;
    if (module.type === 'team_members') {
      const { members: _members, ...teamSettings } = rawConfig as Record<
        string,
        unknown
      > & { members?: unknown };
      onConfigChange({
        ...(module.config ?? {}),
        ...teamSettings,
        members: [],
      });
      return;
    }
    // Preserve template-only fields (e.g. embedded entry `policy`) not in typed views.
    onConfigChange({ ...(module.config ?? {}), ...rawConfig });
  };

  const renderConfigComponent = () => {
    switch (module.type) {
      case 'biography':
        return (
          <BiographyModuleConfigComponent
            config={typedConfig as BiographyModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'contact_info':
        return (
          <ContactInfoModuleConfigComponent
            config={typedConfig as ContactInfoModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'socials':
        return (
          <SocialsModuleConfigComponent
            config={typedConfig as SocialsModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'links':
        return (
          <LinksModuleConfigComponent
            config={typedConfig as LinksModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'video':
        return (
          <VideoModuleConfigComponent
            config={typedConfig as VideoModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'images':
        return (
          <ImagesModuleConfigComponent
            config={typedConfig as ImagesModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'files':
        return (
          <FilesModuleConfigComponent
            config={typedConfig as FilesModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'google_maps':
        return (
          <GoogleMapsModuleConfigComponent
            config={typedConfig as GoogleMapsModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
          />
        );
      case 'team_members':
        return (
          <TeamMembersModuleConfigComponent
            config={typedConfig as TeamMembersModuleConfig}
            onChange={(config) => handleConfigChange(config)}
            {...governanceProps}
            fieldLockedHintMessage={fieldLockedHintMessage}
            templateTeamIds={templateTeamIds}
            showManageTeamLink={showManageTeamLink}
          />
        );
      default:
        return (
          <p className="text-sm text-muted-foreground">Unbekannter Modultyp</p>
        );
    }
  };

  if (variant === 'inline') {
    return <>{renderConfigComponent()}</>;
  }

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        setIsOpen(next);
      }}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="h-8 w-8"
        aria-label="Modul konfigurieren"
      >
        <Settings className="h-4 w-4" />
      </Button>
      <ResponsiveDialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {module.type.charAt(0).toUpperCase() + module.type.slice(1)}{' '}
            konfigurieren
          </ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        <div className="py-4">{renderConfigComponent()}</div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
